import React, { useState } from 'react';
import {
    addResource,
    updateResource,
    deleteResource
} from '../../services/dataService';

const ResourcesSection = ({ siteData, setSiteData, saving, setSaving, showSaveStatus }) => {
    const [editingResources, setEditingResources] = useState({});
    const [savingStates, setSavingStates] = useState({});

    const handleLocalUpdate = (resourceId, field, value) => {
        setEditingResources(prev => ({
            ...prev,
            [resourceId]: {
                ...(prev[resourceId] || siteData.resources.find(r => r.id === resourceId)),
                [field]: value,
                hasChanges: true
            }
        }));
    };

    const handleSaveResource = async (resourceId) => {
        try {
            setSavingStates(prev => ({ ...prev, [resourceId]: true }));
            const updatedResource = editingResources[resourceId];

            await updateResource(resourceId, updatedResource);

            const updatedResources = siteData.resources.map(resource =>
                resource.id === resourceId ? { ...updatedResource, id: resourceId } : resource
            );
            setSiteData(prev => ({ ...prev, resources: updatedResources }));

            // Clear editing state
            setEditingResources(prev => {
                const newState = { ...prev };
                delete newState[resourceId];
                return newState;
            });

            showSaveStatus('Resource updated successfully!');
        } catch (error) {
            showSaveStatus('Failed to update resource', true);
        } finally {
            setSavingStates(prev => ({ ...prev, [resourceId]: false }));
        }
    };

    const handleAddResource = async () => {
        try {
            setSaving(true);
            const newResource = {
                title: 'New Resource',
                description: 'Resource description',
                links: ['Link 1', 'Link 2']
            };

            const resourceId = await addResource(newResource);
            const resourceWithId = { ...newResource, id: resourceId };

            setSiteData(prev => ({
                ...prev,
                resources: [...prev.resources, resourceWithId]
            }));
            showSaveStatus('Resource added successfully!');
        } catch (error) {
            showSaveStatus('Failed to add resource', true);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteResource = async (resourceId) => {
        if (!window.confirm('Are you sure you want to delete this resource?')) {
            return;
        }

        try {
            setSaving(true);
            await deleteResource(resourceId);

            const updatedResources = siteData.resources.filter(resource => resource.id !== resourceId);
            setSiteData(prev => ({ ...prev, resources: updatedResources }));

            // Clear editing state if exists
            setEditingResources(prev => {
                const newState = { ...prev };
                delete newState[resourceId];
                return newState;
            });

            showSaveStatus('Resource deleted successfully!');
        } catch (error) {
            showSaveStatus('Failed to delete resource', true);
        } finally {
            setSaving(false);
        }
    };

    const getResourceData = (resourceId) => {
        return editingResources[resourceId] || siteData.resources.find(r => r.id === resourceId);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-stone-800">Resources</h3>
                <button
                    onClick={handleAddResource}
                    disabled={saving}
                    className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Adding...' : 'Add Resource'}
                </button>
            </div>

            <div className="space-y-4">
                {siteData.resources.map((resource) => {
                    const currentResource = getResourceData(resource.id);
                    const hasChanges = editingResources[resource.id]?.hasChanges;
                    const isSaving = savingStates[resource.id];

                    return (
                        <div key={resource.id} className="bg-stone-50 p-4 rounded-lg">
                            <div className="flex justify-between items-start mb-4">
                                <h4 className="font-medium text-stone-800">Resource: {currentResource.title}</h4>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleSaveResource(resource.id)}
                                        disabled={!hasChanges || isSaving}
                                        className={`px-4 py-1 rounded transition-colors text-sm ${hasChanges && !isSaving
                                            ? 'bg-amber-500 text-white hover:bg-amber-600'
                                            : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                                            }`}
                                    >
                                        {isSaving ? 'Saving...' : hasChanges ? 'Save' : 'Saved'}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteResource(resource.id)}
                                        disabled={saving}
                                        className="text-red-500 hover:text-red-700 disabled:opacity-50 px-2"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={currentResource.title}
                                        onChange={(e) => handleLocalUpdate(resource.id, 'title', e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
                                    <input
                                        type="text"
                                        value={currentResource.description}
                                        onChange={(e) => handleLocalUpdate(resource.id, 'description', e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-stone-700 mb-1">Links (one per line)</label>
                                <textarea
                                    value={Array.isArray(currentResource.links) ? currentResource.links.join('\n') : ''}
                                    onChange={(e) => handleLocalUpdate(resource.id, 'links', e.target.value.split('\n').filter(link => link.trim()))}
                                    className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    rows="3"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ResourcesSection;